/**
 * Ticket status + SLA presentation helpers — SCK-549 PR-8.
 *
 * Kept out of the page component so the same mapping can be reused
 * by SupportTicketDetailPage and covered by focused unit tests.
 */

/**
 * Map a Jira-style ticket status to the MUI Chip color used by the
 * StatusPill on the /support tickets table. Unknown status → default
 * grey so an ad-hoc status string never breaks the layout.
 */
export function statusPillColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "done" || s === "closed" || s === "resolved") return "success";
  if (s === "in progress" || s === "in-progress" || s === "in_progress")
    return "info";
  if (s === "blocked" || s === "escalated") return "error";
  if (s === "open" || s === "to do" || s === "todo" || s === "new")
    return "warning";
  return "default";
}

/**
 * SLA badge classification.
 *
 *   breached   — sla_due is in the past                        (red)
 *   approaching — sla_due is within the next SLA_WARN_MS window (amber)
 *   ontrack    — plenty of runway                               (green)
 *   none       — ticket has no sla_due (already closed, etc.)   (default)
 */
export const SLA_WARN_MS = 4 * 60 * 60 * 1000; // 4 hours

export function slaBadge(slaDueIso, { now = Date.now() } = {}) {
  if (!slaDueIso) return { tone: "none", label: "No SLA" };
  const t = typeof slaDueIso === "number" ? slaDueIso : Date.parse(slaDueIso);
  if (Number.isNaN(t)) return { tone: "none", label: "No SLA" };
  const diff = t - now;
  if (diff < 0) return { tone: "breached", label: "SLA breached" };
  if (diff < SLA_WARN_MS)
    return { tone: "approaching", label: "SLA approaching" };
  return { tone: "ontrack", label: "On track" };
}

/**
 * Map an SLA tone to an MUI Chip color.
 */
export function slaChipColor(tone) {
  if (tone === "breached") return "error";
  if (tone === "approaching") return "warning";
  if (tone === "ontrack") return "success";
  return "default";
}

/**
 * Truncate a summary line so it fits inside a table cell without
 * breaking layout. Long Jira summaries can be paragraph-length.
 */
export function truncate(str, max = 90) {
  if (!str) return "";
  const s = String(str);
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}
